import { useEffect } from 'react';

import { useUrlParams } from '~/hooks/useUrlParams';
import { pageFromOffset } from '~/lib/listParams';

import type {
  Attribute,
  AudienceConfig,
  Environment,
  Product,
  Rule,
  Schedule,
} from '~/components/pages/feature-flags/types';

export { DateField, Select, toOptions } from '~/components/form';
export { errorText, isStale } from '~/lib/apiError';
export { orDash } from '~/lib/orDash';

export interface Option {
  value: string;
  label: string;
}

/** Environments, products and attributes: loaded once by the page and shared by every tab. */
export interface Reference {
  environments: Environment[];
  products: Product[];
  attributes: Attribute[];
  /** A refetch is in flight: a record that is missing from the lists may simply not have arrived yet. */
  pending: boolean;
}

/** Date-only values ('2026-10-19') are calendar days, not instants: format them in UTC so they never shift a day. */

/** `?detail=<kind>:<id>` for one kind of record (opening pushes, closing replaces); `new:<kind>` opens its create form. */
export const useDetail = (kind: string) => {
  const url = useUrlParams();
  const raw = url.get('detail') ?? '';
  const prefix = `${kind}:`;
  return {
    id: raw.startsWith(prefix) && raw.length > prefix.length ? raw.slice(prefix.length) : null,
    isNew: raw === `new:${kind}`,
    open: (id: string) => url.set({ detail: `${prefix}${id}` }, { history: 'push' }),
    /** After a create: the new record replaces the create form in history rather than adding an entry. */
    swap: (id: string) => url.set({ detail: `${prefix}${id}` }),
    close: () => url.set({ detail: null }),
    href: (id: string) => url.href({ detail: `${prefix}${id}` }),
  };
};

/** Old `?offset=` links (Requests, Users) become `?page=`. Returns true while the rewrite is pending, so a list does not fetch the wrong page first. */
export const useLegacyOffset = (pageSize: number): boolean => {
  const url = useUrlParams();
  const offset = url.get('offset');
  useEffect(() => {
    if (offset === null) return;
    const page = pageFromOffset(offset, pageSize);
    url.set({ offset: null, page: url.get('page') ?? (page > 1 ? String(page) : null) });
  }, [offset]);
  return offset !== null;
};

export const newDetail = (kind: string) => `new:${kind}`;

/** Trim and de-blank every condition value, as the service validator will, so dirty checks and payloads agree. */
const cleanRules = (rules: Rule[]): Rule[] =>
  rules.map((r) => ({
    value: r.value,
    conditions: r.conditions.map((c) => ({
      attr: c.attr,
      op: c.op,
      values: Array.from(new Set(c.values.map((v) => v.trim()).filter(Boolean))),
    })),
  }));

export const cleanConfig = <T extends AudienceConfig>(config: T): T => ({
  ...config,
  rules: cleanRules(config.rules),
});

/** The first environment whose Ends is not after its Starts, as a sentence; the service checks the same. */
export const scheduleError = (
  envs: Record<string, Schedule>,
  environments: { key: string; name: string }[],
): string | undefined => {
  const bad = environments.find((e) => {
    const s = envs[e.key];
    return s?.startsAt && s.endsAt && new Date(s.endsAt) <= new Date(s.startsAt);
  });
  return bad && `${bad.name}: Ends must be after Starts`;
};

/** A stable snapshot used to tell whether a draft differs from what was last saved. */
export const snapshot = (value: unknown): string => JSON.stringify(value);
